import { Injectable } from '@angular/core';
import { PropertyService } from '../property.service';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { catchError, filter, finalize, startWith, switchMap } from 'rxjs/operators';
import { PropertyStoreService } from './property-store.service';
import { Mention } from '../../domain/mention';
import { IRefreshable } from './irefreshable';
import { LoadingState } from '../../domain/loading-state';
import { LoadingOverlayStoreService } from './loading-overlay-store.service';
import { DataStoreService } from '../data-store.service';
import { RefreshDataState } from '../../domain/refresh-data-state';

@Injectable({
   providedIn: 'root'
})
export class MentionStoreService implements IRefreshable {
   private _mentions: BehaviorSubject<Mention[]> = new BehaviorSubject(
      new Array<Mention>()
   );

   public readonly mentions: Observable<Mention[]> = this._mentions.asObservable();

   private currentPropertyId: number | undefined;

   constructor(
      private propertyStore: PropertyStoreService,
      private propertyService: PropertyService,
      private loadingOverlayStore: LoadingOverlayStoreService,
      private dataStoreService: DataStoreService
   ) {
      this.propertyStore.currentProperty
         .pipe(
            switchMap((property) => {
               this.currentPropertyId = property.id;
               // Never show the previous property's mentions while loading.
               this._mentions.next([]);

               return this.dataStoreService.refreshTaskDataSubject.pipe(
                  filter((state) => state.Mentions),
                  startWith(null),
                  // Cancel superseded HTTP requests on refresh or property change.
                  switchMap(() => this.loadMentions(property.id))
               );
            })
         )
         .subscribe((mentions) => this._mentions.next(mentions));
   }

   readMentions(mentionIds: number[]): Observable<number> {
      if (this.currentPropertyId != undefined) {
         return this.propertyService.readMentions(this.currentPropertyId, mentionIds);
      }
      return of(0);
   }

   deleteMentions(mentionIds: number[]): Observable<number> {
      if (this.currentPropertyId != undefined && mentionIds.length > 0) {
         return this.propertyService.deleteMentions(
            this.currentPropertyId,
            mentionIds
         );
      }
      return of(0);
   }

   refreshStore(): void {
      const refreshState = new RefreshDataState();
      refreshState.Mentions = true;
      this.dataStoreService.refreshTaskData(refreshState);
   }

   private loadMentions(propertyId: number): Observable<Mention[]> {
      const loadingState = new LoadingState();
      loadingState.Mentions = true;
      this.loadingOverlayStore.setLoadingState(loadingState);

      return this.propertyService.getMentions(propertyId).pipe(
         catchError((error: unknown) => {
            console.error('Unable to refresh mentions', error);
            return EMPTY;
         }),
         finalize(() => {
            loadingState.Mentions = false;
            this.loadingOverlayStore.setLoadingState(loadingState);
         })
      );
   }
}
